import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { AuthContext } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const Home = () => {
  const { currentUser, invitations } = useContext(AppContext);
  const { isAdmin, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, loading, navigate]);

  const sent = invitations.filter(i => i.direction === 'sent');
  const received = invitations.filter(i => i.direction === 'received');
  const replies = invitations.filter(i => i.direction === 'sent' && (i.reply_message || i.reply_response));

  const handleActivityClick = (inv) => {
    if (inv.direction === 'sent' && (inv.reply_message || inv.reply_response)) {
      navigate(`/reply/${inv.unique_token}`);
    } else if (inv.direction === 'received') {
      navigate(`/invite/${inv.unique_token}`);
    } else if (inv.direction === 'sent') {
      navigate('/invitations');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 h-full bg-white">
      <header className="flex justify-between items-center mb-8 pt-4">
        <div>
          <img src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png" alt="DayClap" className="h-8 mb-1 object-contain" />
          <p className="text-gray-500 text-sm">
            Hi {currentUser?.name || 'there'}, create something special today ❤️
          </p>
        </div>
        <button onClick={() => navigate('/profile')}>
          <img
            src={currentUser?.avatar_url || currentUser?.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
            alt="Profile"
            className="w-10 h-10 rounded-full border border-gray-200"
          />
        </button>
      </header>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { label: 'Sent', value: sent.length, icon: 'Send', color: 'text-pink-500' },
          { label: 'Received', value: received.length, icon: 'Mail', color: 'text-purple-500' },
          { label: 'Replies', value: replies.length, icon: 'MessageCircle', color: 'text-emerald-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-50 p-3 rounded-xl text-center">
            <SafeIcon name={stat.icon} className={`${stat.color} text-lg mx-auto mb-1`} />
            <div className="text-lg font-bold text-gray-800">{stat.value}</div>
            <div className="text-[10px] text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Two Paths */}
      <h2 className="text-lg font-bold text-gray-800 mb-4">Choose Your Experience</h2>

      <div className="space-y-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          onClick={() => navigate('/create/singles')}
          className="relative overflow-hidden p-6 rounded-2xl bg-gradient-to-br from-pink-500 via-rose-500 to-red-500 text-white cursor-pointer shadow-lg shadow-pink-200 hover:shadow-xl transition-all group"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-8 -translate-x-8" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl">💌</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Singles</h3>
                <p className="text-pink-100 text-xs">Fun invitation game</p>
              </div>
            </div>
            <p className="text-pink-100 text-sm mb-4 leading-relaxed">
              Create a playful invitation with music and a question. See if they say yes! 🎉
            </p>
            <div className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all">
              Create Invitation <SafeIcon name="ArrowRight" className="text-sm" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate('/create/couples')}
          className="relative overflow-hidden p-6 rounded-2xl bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-500 text-white cursor-pointer shadow-lg shadow-purple-200 hover:shadow-xl transition-all group"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-8 -translate-x-8" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl">💕</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Couples</h3>
                <p className="text-purple-100 text-xs">Digital love card</p>
              </div>
            </div>
            <p className="text-purple-100 text-sm mb-4 leading-relaxed">
              Send a romantic message with photos, music, and an optional date plan. ✨
            </p>
            <div className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all">
              Create Love Card <SafeIcon name="ArrowRight" className="text-sm" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      {invitations.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">Recent Activity</h2>
            <button onClick={() => navigate('/invitations')} className="text-pink-500 text-sm font-semibold">
              View All
            </button>
          </div>
          <div className="space-y-2">
            {invitations.slice(0, 5).map((inv) => {
              const isReply = inv.direction === 'sent' && (inv.reply_message || inv.reply_response);
              return (
                <motion.div
                  key={inv.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={() => handleActivityClick(inv)}
                  className="bg-gray-50 p-3 rounded-xl flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-colors active:bg-gray-200"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isReply ? 'bg-emerald-100' :
                    inv.flow_type === 'couples' ? 'bg-purple-100 text-purple-500' : 'bg-pink-100 text-pink-500'
                  }`}>
                    <span className="text-lg">
                      {isReply ? '💌' : inv.flow_type === 'couples' ? '💕' : '💌'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {isReply
                        ? `Reply from ${inv.recipient_name}`
                        : inv.direction === 'sent'
                          ? `To ${inv.recipient_name}`
                          : `From ${inv.sender_profile?.name || 'Someone'}`}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{inv.occasion}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                    inv.status === 'replied' ? 'bg-emerald-100 text-emerald-700' :
                    inv.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    inv.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {inv.status === 'replied' ? '💌 Reply' : inv.status}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Home;